import { QuoteStatus, type Quote } from '@/types';

/**
 * Pipeline-afleiding voor het salesoverzicht.
 *
 * Alles hier komt uit velden die al in de database staan: status, expiresAt,
 * viewCount, de afgeleide handtekeningstatus en het bedrag uit de
 * investeringsblokken. Er wordt niets extra opgeslagen en niets geraden — een
 * offerte zonder bedragblokken heeft waarde 0, en dat betekent 'nog geen
 * investering ingevuld', niet 'onbekend'.
 */

export type PipelineStageId =
  | 'concept'
  | 'gepubliceerd'
  | 'bekeken'
  | 'ondertekening'
  | 'geaccepteerd';

export const PIPELINE_STAGE_IDS: PipelineStageId[] = [
  'concept',
  'gepubliceerd',
  'bekeken',
  'ondertekening',
  'geaccepteerd',
];

export const PIPELINE_STAGE_LABELS: Record<PipelineStageId, string> = {
  concept: 'Concept',
  gepubliceerd: 'Gepubliceerd',
  bekeken: 'Bekeken',
  ondertekening: 'Ondertekening',
  geaccepteerd: 'Geaccepteerd',
};

/** Zelfde badge-varianten als het offerteoverzicht, zodat een status overal dezelfde kleur heeft. */
export const PIPELINE_STAGE_VARIANTS: Record<
  PipelineStageId,
  'default' | 'secondary' | 'success' | 'warning' | 'info'
> = {
  concept: 'secondary',
  gepubliceerd: 'info',
  bekeken: 'default',
  ondertekening: 'warning',
  geaccepteerd: 'success',
};

export const PIPELINE_STAGE_HINTS: Record<PipelineStageId, string> = {
  concept: 'Aangemaakt, nog niet gepubliceerd.',
  gepubliceerd: 'Publieke link staat klaar, nog niet geopend door de klant.',
  bekeken: 'De klant heeft de offerte geopend.',
  ondertekening:
    'Er is al getekend, maar nog niet door iedereen — klant-tekenbevoegden of de UBO-medeondertekening ontbreekt.',
  geaccepteerd: 'Volledig ondertekend, inclusief medeondertekening.',
};

const DAY_MS = 24 * 60 * 60 * 1000;
export const EXPIRING_SOON_DAYS = 7;

const toTime = (value: string | Date | undefined) =>
  value ? new Date(value).getTime() : null;

/**
 * Er is al getekend maar nog niet door iedereen: òf een deel van de
 * klant-tekenbevoegden, òf de klant is klaar en de UBO moet nog medeondertekenen.
 */
export function isSigningInProgress(quote: Quote): boolean {
  const summary = quote.signatureSummary;
  if (!summary) return false;
  if (summary.countersignRequired) return true;

  const signed = summary.recipientsSigned ?? 0;
  const total = summary.recipientsTotal ?? 0;
  return signed > 0 && signed < total;
}

/** Klant is klaar, de UBO-handtekening ontbreekt nog. */
export function needsUboCountersign(quote: Quote): boolean {
  return Boolean(quote.signatureSummary?.countersignRequired);
}

/** Heeft de klant de offerte geopend? */
export function hasBeenOpened(quote: Quote): boolean {
  return quote.status === QuoteStatus.VIEWED || (quote.viewCount ?? 0) > 0;
}

/**
 * In welke kolom hoort deze offerte? `null` = niet op het bord: afgewezen,
 * verlopen en ingetrokken offertes lopen niet meer, die staan in de signalen
 * en in de bestaande statustabs.
 *
 * Het onderscheid tussen Gepubliceerd en Bekeken komt uit `viewCount`, niet uit
 * de status VIEWED. In de praktijk blijven geopende offertes op `sent` staan,
 * en dan zou de kolom Bekeken altijd leeg blijven terwijl de klant allang heeft
 * gekeken.
 */
export function getPipelineStage(quote: Quote): PipelineStageId | null {
  switch (quote.status) {
    case QuoteStatus.DRAFT:
      return 'concept';
    case QuoteStatus.SENT:
    case QuoteStatus.VIEWED:
      if (isSigningInProgress(quote)) return 'ondertekening';
      return hasBeenOpened(quote) ? 'bekeken' : 'gepubliceerd';
    case QuoteStatus.ACCEPTED:
      return isSigningInProgress(quote) ? 'ondertekening' : 'geaccepteerd';
    default:
      return null;
  }
}

export const quoteValueCents = (quote: Quote) => quote.valueCents ?? 0;

const sumValue = (quotes: Quote[]) =>
  quotes.reduce((total, quote) => total + quoteValueCents(quote), 0);

/** Dagen tot de vervaldatum; negatief zodra die voorbij is. `null` zonder vervaldatum. */
export function daysUntilExpiry(quote: Quote, now: number): number | null {
  const expires = toTime(quote.expiresAt);
  if (expires === null) return null;
  return Math.ceil((expires - now) / DAY_MS);
}

export type PipelineSignalId =
  | 'verloopt'
  | 'nooit-geopend'
  | 'medeondertekening'
  | 'spook'
  | 'verouderd';

export interface PipelineSignal {
  id: PipelineSignalId;
  label: string;
  count: number;
  valueCents: number;
  variant: 'secondary' | 'warning' | 'info';
  hint: string;
  quoteIds: string[];
}

export interface PipelineStage {
  id: PipelineStageId;
  label: string;
  quotes: Quote[];
  valueCents: number;
}

export interface PipelineMonth {
  /** Eerste dag van de maand, handig als React-key en voor labels. */
  key: string;
  label: string;
  valueCents: number;
}

export interface PipelineData {
  stages: PipelineStage[];
  signals: PipelineSignal[];
  /** Verstuurd, bekeken en in ondertekening samen: het geld dat nog kan vallen. */
  openValueCents: number;
  openCount: number;
  /** Verlopen offertes met openstaande waarde. */
  riskValueCents: number;
  riskCount: number;
  averageOpenValueCents: number;
  acceptedThisMonthValueCents: number;
  acceptedThisMonthCount: number;
  /** Deel daarvan dat ook door de UBO is medeondertekend. */
  acceptedThisMonthFullySigned: number;
  acceptedPreviousMonthValueCents: number;
  monthlyAccepted: PipelineMonth[];
  /** Geaccepteerd gedeeld door alles wat een uitkomst heeft (geaccepteerd, afgewezen, verlopen). */
  winRate: number | null;
  decidedCount: number;
  acceptedCount: number;
}

const MONTH_LABELS = [
  'jan',
  'feb',
  'mrt',
  'apr',
  'mei',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];

const monthKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/**
 * Geaccepteerde waarde per maand over de laatste `months` maanden.
 *
 * De acceptatiedatum zelf staat niet apart in de database; `updatedAt` van een
 * geaccepteerde offerte is het moment van tekenen, tenzij er daarna nog aan is
 * gewerkt. Daarom is dit een trendlijn, geen boekhouding.
 */
export function buildMonthlyAccepted(
  quotes: Quote[],
  now: number,
  months = 6,
): PipelineMonth[] {
  const reference = new Date(now);
  const buckets: PipelineMonth[] = [];
  const indexByKey = new Map<string, number>();

  for (let offset = months - 1; offset >= 0; offset -= 1) {
    const date = new Date(reference.getFullYear(), reference.getMonth() - offset, 1);
    const key = monthKey(date);
    indexByKey.set(key, buckets.length);
    buckets.push({ key, label: MONTH_LABELS[date.getMonth()], valueCents: 0 });
  }

  for (const quote of quotes) {
    if (quote.status !== QuoteStatus.ACCEPTED) continue;
    const index = indexByKey.get(monthKey(new Date(quote.updatedAt)));
    if (index === undefined) continue;
    buckets[index].valueCents += quoteValueCents(quote);
  }

  return buckets;
}

export function buildPipeline(quotes: Quote[], now: number = Date.now()): PipelineData {
  const byStage = new Map<PipelineStageId, Quote[]>(
    PIPELINE_STAGE_IDS.map((id) => [id, [] as Quote[]]),
  );
  for (const quote of quotes) {
    const stage = getPipelineStage(quote);
    if (stage) byStage.get(stage)!.push(quote);
  }

  const stages: PipelineStage[] = PIPELINE_STAGE_IDS.map((id) => {
    const stageQuotes = byStage.get(id)!;
    return {
      id,
      label: PIPELINE_STAGE_LABELS[id],
      quotes: stageQuotes,
      valueCents: sumValue(stageQuotes),
    };
  });

  const openQuotes = [
    ...byStage.get('gepubliceerd')!,
    ...byStage.get('bekeken')!,
    ...byStage.get('ondertekening')!,
  ];
  const openValueCents = sumValue(openQuotes);

  const expiredQuotes = quotes.filter((quote) => quote.status === QuoteStatus.EXPIRED);
  const declinedCount = quotes.filter((quote) => quote.status === QuoteStatus.DECLINED).length;

  const acceptedQuotes = quotes.filter((quote) => quote.status === QuoteStatus.ACCEPTED);
  const reference = new Date(now);
  const thisMonth = monthKey(reference);
  const previousMonth = monthKey(
    new Date(reference.getFullYear(), reference.getMonth() - 1, 1),
  );

  const acceptedThisMonth = acceptedQuotes.filter(
    (quote) => monthKey(new Date(quote.updatedAt)) === thisMonth,
  );
  const acceptedPreviousMonth = acceptedQuotes.filter(
    (quote) => monthKey(new Date(quote.updatedAt)) === previousMonth,
  );

  const decidedCount = acceptedQuotes.length + declinedCount + expiredQuotes.length;

  const expiringSoon = openQuotes.filter((quote) => {
    const days = daysUntilExpiry(quote, now);
    return days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS;
  });
  const neverOpened = byStage.get('gepubliceerd')!;
  const awaitingCountersign = quotes.filter(needsUboCountersign);
  const ghosts = quotes.filter((quote) => quote.isGhost === true);
  const outdated = quotes.filter((quote) => (quote.outdatedProductRows ?? 0) > 0);

  const toSignal = (
    id: PipelineSignalId,
    label: string,
    variant: PipelineSignal['variant'],
    hint: string,
    matched: Quote[],
  ): PipelineSignal => ({
    id,
    label,
    variant,
    hint,
    count: matched.length,
    valueCents: sumValue(matched),
    quoteIds: matched.map((quote) => quote.id),
  });

  const signals: PipelineSignal[] = [
    toSignal(
      'verloopt',
      `Verloopt binnen ${EXPIRING_SOON_DAYS} dagen`,
      'warning',
      'Openstaande offertes waarvan de vervaldatum binnen een week valt.',
      expiringSoon,
    ),
    toSignal(
      'nooit-geopend',
      'Verstuurd, nooit geopend',
      'info',
      'De publieke link staat klaar, maar is nog nooit door de klant geopend.',
      neverOpened,
    ),
    toSignal(
      'medeondertekening',
      'Wacht op medeondertekening UBO',
      'warning',
      'De klant heeft getekend; de UBO-handtekening ontbreekt nog.',
      awaitingCountersign,
    ),
    toSignal(
      'spook',
      'Spookoffertes',
      'secondary',
      'Concepten die minstens 24 uur geleden zijn aangemaakt en daarna nooit zijn geopend of bewerkt.',
      ghosts,
    ),
    toSignal(
      'verouderd',
      'Op verouderde productversie',
      'secondary',
      'Bevat regels die op een oudere versie van het product staan dan de nu gepubliceerde.',
      outdated,
    ),
  ].filter((signal) => signal.count > 0);

  return {
    stages,
    signals,
    openValueCents,
    openCount: openQuotes.length,
    riskValueCents: sumValue(expiredQuotes),
    riskCount: expiredQuotes.length,
    averageOpenValueCents: openQuotes.length
      ? Math.round(openValueCents / openQuotes.length)
      : 0,
    acceptedThisMonthValueCents: sumValue(acceptedThisMonth),
    acceptedThisMonthCount: acceptedThisMonth.length,
    acceptedThisMonthFullySigned: acceptedThisMonth.filter(
      (quote) => !needsUboCountersign(quote),
    ).length,
    acceptedPreviousMonthValueCents: sumValue(acceptedPreviousMonth),
    monthlyAccepted: buildMonthlyAccepted(quotes, now),
    winRate: decidedCount ? acceptedQuotes.length / decidedCount : null,
    decidedCount,
    acceptedCount: acceptedQuotes.length,
  };
}
